/**
 * Central catalog of player data fields.
 *
 * PlayerDataFieldDef is the single source of truth used to build:
 *   - Roster grid columns      (GroupEditor DataGridPro)
 *   - Registration form fields (RegistrationTab / RegistrationPopout)
 *   - Upload template columns  (CSV player import)
 *
 * Adding a field here automatically makes it available in all three contexts
 * based on the inRoster / inRegistration / inUpload flags.
 */

import type { PlayerDataFieldDef } from '@moment-golf/types';
import type { RegistrationFieldItem, CourseTeeSetData } from '@moment-golf/types';
import type { GridColDef } from '@mui/x-data-grid-pro';

// ---------------------------------------------------------------------------
// Field catalog
// ---------------------------------------------------------------------------

export const allPlayerDataFields: PlayerDataFieldDef[] = [
    // --- Core identity (always present as roster columns, non-removable in UI) ---
    {
        key: 'playerName',
        label: 'Player Name',
        type: 'text',
        required: true,
        inRoster: true,
        inRegistration: true,
        inUpload: true,
        rosterWidth: 180,
        rosterEditable: true,
    },
    {
        key: 'playerEmail',
        label: 'Email',
        type: 'email',
        required: true,
        inRoster: true,
        inRegistration: true,
        inUpload: true,
        rosterWidth: 220,
        rosterEditable: true,
    },

    // --- Contact ---
    {
        key: 'playerPhone',
        label: 'Phone',
        type: 'tel',
        required: false,
        inRoster: true,
        inRegistration: true,
        inUpload: true,
        rosterWidth: 140,
        rosterEditable: true,
    },

    // --- Address ---
    {
        key: 'address',
        label: 'Address',
        type: 'text',
        required: false,
        inRoster: true,
        inRegistration: true,
        inUpload: true,
        rosterWidth: 200,
        rosterEditable: true,
    },
    {
        key: 'city',
        label: 'City',
        type: 'text',
        required: false,
        inRoster: true,
        inRegistration: true,
        inUpload: true,
        rosterWidth: 140,
        rosterEditable: true,
    },
    {
        key: 'state',
        label: 'State',
        type: 'text',
        required: false,
        inRoster: true,
        inRegistration: true,
        inUpload: true,
        rosterWidth: 80,
        rosterEditable: true,
    },
    {
        key: 'zip',
        label: 'Zip',
        type: 'text',
        required: false,
        inRoster: true,
        inRegistration: true,
        inUpload: true,
        rosterWidth: 90,
        rosterEditable: true,
    },

    // --- Handicap / GHIN ---
    {
        key: 'playerHandicap',
        label: 'Handicap Index',
        type: 'number',
        required: false,
        inRoster: true,
        inRegistration: true,
        inUpload: true,
        rosterWidth: 120,
        rosterEditable: true,
    },
    {
        key: 'ghinNumber',
        label: 'GHIN Number',
        type: 'text',
        required: false,
        inRoster: true,
        inRegistration: true,
        inUpload: true,
        rosterWidth: 130,
        rosterEditable: true,
    },

    // --- Gender ---
    {
        key: 'gender',
        label: 'Gender',
        type: 'select',
        required: false,
        options: ['Male', 'Female', 'Other'],
        inRoster: true,
        inRegistration: true,
        inUpload: true,
        rosterWidth: 100,
        rosterEditable: true,
    },

    // --- Tee set (event-sourced from course data) ---
    {
        key: 'teeSet',
        label: 'Tee Set',
        type: 'select',
        required: false,
        eventSource: 'teeSet',
        inRoster: true,
        inRegistration: true,
        inUpload: true,
        rosterWidth: 130,
        rosterEditable: true,
    },

    // --- Smart tee helpers ---
    {
        key: 'approxCourseRating',
        label: 'Approx Course Rating',
        type: 'number',
        required: false,
        teeSetMode: 'smart',
        inRoster: true,
        inRegistration: false,
        inUpload: true,
        rosterWidth: 160,
        rosterEditable: true,
    },
    {
        key: 'approxLength',
        label: 'Approx Length (yds)',
        type: 'number',
        required: false,
        teeSetMode: 'smart',
        inRoster: true,
        inRegistration: false,
        inUpload: true,
        rosterWidth: 160,
        rosterEditable: true,
    },

    // --- Event-sourced grouping ---
    {
        key: 'division',
        label: 'Division',
        type: 'select',
        required: false,
        eventSource: 'division',
        eventLabelField: 'divisionName',
        regLevelData: true,
        inRoster: true,
        inRegistration: true,
        inUpload: true,
        rosterWidth: 130,
        rosterEditable: true,
    },
    {
        key: 'flight',
        label: 'Flight',
        type: 'select',
        required: false,
        eventSource: 'flight',
        eventLabelField: 'flightName',
        regLevelData: true,
        inRoster: true,
        inRegistration: true,
        inUpload: true,
        rosterWidth: 110,
        rosterEditable: true,
    },
    {
        key: 'teamName',
        label: 'Team Name',
        type: 'text',
        required: false,
        regLevelData: true,
        inRoster: true,
        inRegistration: true,
        inUpload: true,
        rosterWidth: 150,
        rosterEditable: true,
    },

    // --- Collection admin input (roster-only; not synced from registration) ---
    {
        key: 'rewardPoints',
        label: 'Rewards',
        type: 'number',
        required: false,
        inRoster: true,
        inRegistration: false,
        inUpload: true,
        rosterWidth: 110,
        rosterEditable: true,
    },

    // --- Referral ---
    {
        key: 'referralCode',
        label: 'Referral Code',
        type: 'text',
        required: false,
        inRoster: false,
        inRegistration: true,
        inUpload: false,
    },

    // --- Policies (checkbox fields, modeled on liability waiver) ---
    {
        key: 'tournamentEtiquettePolicy',
        label: 'Tournament Etiquette Policy',
        type: 'checkbox',
        required: false,
        fileURL: '',
        inRoster: false,
        inRegistration: true,
        inUpload: false,
    },
    {
        key: 'mediaPolicy',
        label: 'Media Policy',
        type: 'checkbox',
        required: false,
        fileURL: '',
        inRoster: false,
        inRegistration: true,
        inUpload: false,
    },

    // --- Tee Group Focus ---
    {
        key: 'teeGroupFocusPreference',
        label: 'Tee Group Focus Preference',
        type: 'select',
        required: false,
        options: ['Competition', 'Vibes', 'No Preference'],
        inRoster: true,
        inRegistration: true,
        inUpload: true,
        rosterWidth: 180,
        rosterEditable: true,
    },
];

/** Fields available as roster columns */
export const rosterFields = allPlayerDataFields.filter(f => f.inRoster);

/** Fields available in registration forms (static options only — no eventSource) */
export const registrationStaticFields = allPlayerDataFields.filter(
    f => f.inRegistration && !f.eventSource,
);

/** Fields available in registration forms that are event-sourced */
export const registrationEventSourcedFields = allPlayerDataFields.filter(
    f => f.inRegistration && !!f.eventSource,
);

/** Fields available for upload templates */
export const uploadFields = allPlayerDataFields.filter(f => f.inUpload);

// ---------------------------------------------------------------------------
// Utility: resolve tee set options from loaded course data
// ---------------------------------------------------------------------------

/**
 * Returns the unique tee set names from all provided courses.
 * Used to populate the teeSet select column in the roster grid.
 */
export function resolveTeeSetOptions(courseData: CourseTeeSetData[]): string[] {
    const names = new Set<string>();
    for (const course of courseData) {
        for (const ts of course.teeSets ?? []) {
            if (ts.teeName) names.add(ts.teeName);
        }
    }
    return Array.from(names);
}

// ---------------------------------------------------------------------------
// Utility: convert PlayerDataFieldDef → RegistrationFieldItem
// ---------------------------------------------------------------------------

/**
 * Maps a PlayerDataFieldDef to the RegistrationFieldItem shape used by
 * existing registration form components.
 */
export function toRegistrationFieldItem(def: PlayerDataFieldDef): RegistrationFieldItem {
    return {
        name: def.key,
        label: def.label,
        field: def.type,
        type: def.type,
        required: def.required ?? false,
        ...(def.options ? { options: def.options } : {}),
        ...(def.fileURL != null ? { fileURL: def.fileURL } : {}),
        ...(def.eventSource ? { eventSource: def.eventSource as 'division' | 'flight' } : {}),
        ...(def.eventLabelField ? { eventLabelField: def.eventLabelField } : {}),
        ...(def.regLevelData ? { regLevelData: def.regLevelData } : {}),
        ...(def.tooltip != null ? { tooltip: def.tooltip } : {}),
    };
}

// ---------------------------------------------------------------------------
// Utility: build DataGridPro column definitions for the roster
// ---------------------------------------------------------------------------

/**
 * Builds a GridColDef[] for the roster DataGridPro from a list of enabled
 * field keys. Automatically resolves singleSelect valueOptions and column types.
 *
 * @param enabledKeys  Array of PlayerDataFieldDef.key values to include
 * @param runtimeOptions  Runtime-resolved option lists for event-sourced select fields
 */
export function buildRosterColumns(
    enabledKeys: string[],
    runtimeOptions: {
        teeSetOptions?: string[];
        divisionOptions?: string[];
        flightOptions?: string[];
    } = {},
): GridColDef[] {
    const fieldMap = new Map(allPlayerDataFields.map(f => [f.key, f]));

    return enabledKeys
        .map(key => {
            const def = fieldMap.get(key);
            if (!def) return null;

            const editable = def.rosterEditable !== false;
            const col: GridColDef = {
                field: def.key,
                headerName: def.label,
                width: def.rosterWidth ?? 140,
                editable,
            };

            if (def.type === 'select') {
                let valueOptions: string[] = def.options ?? [];
                if (def.eventSource === 'teeSet') valueOptions = runtimeOptions.teeSetOptions ?? [];
                if (def.eventSource === 'division') valueOptions = runtimeOptions.divisionOptions ?? [];
                if (def.eventSource === 'flight') valueOptions = runtimeOptions.flightOptions ?? [];

                col.type = 'singleSelect';
                (col as any).valueOptions = valueOptions;
            }

            if (def.type === 'number') {
                col.type = 'number';
            }

            return col;
        })
        .filter((col): col is GridColDef => col !== null);
}

// ---------------------------------------------------------------------------
// Helpers for upload templates
// ---------------------------------------------------------------------------

/** Returns upload template column definitions in the shape UploadTemplates.json uses */
export function buildUploadTemplateColumns(fieldKeys?: string[]): Array<{ key: string; label: string; type: string }> {
    const fields = fieldKeys
        ? fieldKeys.map(k => allPlayerDataFields.find(f => f.key === k)).filter(Boolean) as PlayerDataFieldDef[]
        : uploadFields;

    return fields.map(f => ({
        key: f.key,
        label: f.label,
        type: f.type === 'select' ? 'singleSelect' : f.type,
    }));
}
